/**
 * The intro tour, drawn: the rail button and the step dialog.
 *
 * The rules (when it starts, what counts as seen, when the button is
 * highlighted) are in `tour-rules.ts`; each console supplies its own steps, already
 * narrowed to the screens the signed-in person's role can open, and its own
 * pair of server calls.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { Icon } from './icons.tsx';
import { kitText } from './messages.ts';
import { showErrorToast } from './request-success.ts';
import type { TourLayout } from './tour-layout.ts';
import { TourLayoutView } from './tour-layout-view.tsx';
import { TourController, type TourState, type TourStore } from './tour-rules.ts';
import { Modal } from './ui.tsx';

export interface TourStep {
  /** Stable, for React. */
  readonly id: string;
  readonly title: string;
  readonly body: string;
  /** The tour version this step arrived in. Absent: the first version. */
  readonly since?: number;
  /** A live miniature of the person's own console, drawn above the text. */
  readonly layout?: TourLayout;
  /**
   * A way to the screen the step describes. Only given when this person's
   * role can open it; going there ends the tour (recorded as seen).
   */
  readonly go?: { readonly label: string; readonly onGo: () => void };
}

export interface TourHandle {
  readonly state: TourState;
  readonly steps: readonly TourStep[];
  readonly controller: TourController;
}

/**
 * One tour for one console. `steps` may change between renders (a role read
 * after boot); the controller always asks for the current list.
 */
export function useTour({
  version,
  store,
  steps,
}: {
  version: number;
  store: TourStore;
  steps: readonly TourStep[];
}): TourHandle {
  const current = useRef(steps);
  current.current = steps;
  const [controller] = useState(() => {
    const c: TourController = new TourController({
      version,
      store,
      steps: () => current.current,
      // While the dialog is open it says so itself; a toast on top would be
      // the same sentence twice.
      onSaveFailed: () => {
        if (!c.getState().open) showErrorToast(kitText('tour.saveFailed'));
      },
    });
    return c;
  });
  useEffect(() => {
    void controller.init();
  }, [controller]);
  const state = useSyncExternalStore(controller.subscribe, controller.getState, controller.getState);
  return { state, steps, controller };
}

/**
 * The rail's tour button. Highlighted — a dot, and a name that says why —
 * when the console's tour has steps this person has not seen yet.
 */
export function TourButton({ tour }: { tour: TourHandle }) {
  return (
    <button
      type="button"
      className="tour-button"
      aria-haspopup="dialog"
      {...(tour.state.highlight ? { 'data-new': 'true' } : {})}
      onClick={() => tour.controller.start()}
    >
      <Icon name="tour" className="glyph" />
      <span className="nav-label">{kitText('tour.open')}</span>
      {tour.state.highlight ? (
        <>
          <span className="nav-dot" aria-hidden="true" />
          <span className="sr-only">{kitText('tour.newSteps')}</span>
        </>
      ) : null}
    </button>
  );
}

/**
 * Mounted once beside the shell. Renders nothing while the tour is closed.
 *
 * Focus goes into the dialog and comes back to the button that opened it
 * (`Modal` does both, and traps Tab in between). On a phone that button is in
 * the closed menu sheet, so focus falls back to the menu button instead.
 */
export function TourDialog({ tour }: { tour: TourHandle }) {
  const { state, steps, controller } = tour;
  const wasOpen = useRef(false);
  const nextButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (state.open) {
      // Onto Next rather than the first control (Skip): Enter should move the
      // tour on, not end it.
      if (!wasOpen.current) nextButton.current?.focus();
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const t = window.setTimeout(() => {
      const here = document.activeElement;
      if (here && here !== document.body && (here as HTMLElement).offsetParent !== null) return;
      const visible = (sel: string) =>
        Array.from(document.querySelectorAll<HTMLElement>(sel)).find((el) => el.offsetParent !== null);
      (visible('.tour-button') ?? visible('.rail-menu-button'))?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [state.open]);

  if (!state.open) return null;
  const index = Math.min(state.step, steps.length - 1);
  const step = steps[index];
  if (!step) return null;
  const last = index === steps.length - 1;

  return (
    <Modal
      title={step.title}
      onClose={() => controller.exit()}
      closeLabel={kitText('tour.exit')}
      className={step.layout ? 'tour tour-has-layout' : 'tour'}
      keepOnBackdropClick
    >
      <div className="tour-step" aria-live="polite">
        <p className="tour-progress">
          {kitText('tour.progress', { n: String(index + 1), total: String(steps.length) })}
          {state.newAfter !== null && (step.since ?? 1) > state.newAfter ? (
            <span className="tour-new">{kitText('tour.new')}</span>
          ) : null}
        </p>
        {step.layout ? <TourLayoutView layout={step.layout} /> : null}
        <p className="tour-body">{step.body}</p>
        {step.go ? (
          <button
            type="button"
            className="link-button tour-go"
            onClick={() => {
              const go = step.go!;
              controller.exit();
              go.onGo();
            }}
          >
            {step.go.label}
          </button>
        ) : null}
        {state.saveFailed ? (
          <p className="tour-notice" role="status">{kitText('tour.saveFailed')}</p>
        ) : null}
      </div>
      <div className="form-actions tour-actions">
        <button type="button" className="copy" onClick={() => controller.skip()}>
          {kitText('tour.skip')}
        </button>
        <span className="tour-actions-end">
          {index > 0 ? (
            <button type="button" className="copy" onClick={() => controller.back()}>
              {kitText('tour.back')}
            </button>
          ) : null}
          <button ref={nextButton} type="button" className="primary" onClick={() => controller.next()}>
            {kitText(last ? 'tour.done' : 'tour.next')}
          </button>
        </span>
      </div>
    </Modal>
  );
}
